import type { Lead } from 'services/types/lead';

import baseApi from './baseApi';

interface LeadsResponse {
  rows: Lead[];
  count: number;
}

interface KanbanLeadsResponse {
  leadsByStatus: Record<string, Lead[]>;
  totalLeads: number;
  statusCounts: Record<string, number>;
  paginationInfo: Record<
    string,
    {
      page: number;
      hasMore: boolean;
      totalCount: number;
    }
  >;
  pageSize: number;
}

interface KanbanStatusCountsResponse {
  statusCounts: Record<string, number>;
  totalLeads: number;
}

export interface MetaCampaignInfo {
  metaCampaignId: string | null;
  metaCampaignName: string | null;
  metaAdSetId: string | null;
  metaAdSetName: string | null;
  metaAdId: number;
  metaAdName: string | null;
}

export interface WhatsAppCampaignStatsRow {
  whatsAppCampaignId: number;
  whatsAppCampaignName: string | null;
  totalLeads: number;
  leadsWithMessagesSent: number;
  leadsWithClientResponses: number;
  leadsUploadedToCRM: number;
}

export interface MetaCampaignStatsRow {
  metaAdId: string;
  metaAdName: string | null;
  metaAdSetId: string | null;
  metaAdSetName: string | null;
  metaCampaignId: string | null;
  metaCampaignName: string | null;
  totalLeads: number;
  leadsWithMessagesSent: number;
  leadsWithClientResponses: number;
  leadsWithHighMessageCount: number;
  leadsUploadedToCRM: number;
}

export interface CampaignStatsResponse {
  whatsAppCampaignStats: WhatsAppCampaignStatsRow[];
  metaCampaignStats: MetaCampaignStatsRow[];
  totalStats: {
    totalLeads: number;
    leadsWithMessagesSent: number;
    leadsWithClientResponses: number;
    leadsUploadedToCRM: number;
  };
  totalCampaigns: number;
}

export interface CampaignSelectorResponse {
  whatsAppCampaigns: Array<{
    id: number;
    name: string;
    type: 'whatsapp';
  }>;
  metaAds: Array<{
    id: string;
    name: string;
    type: 'meta';
    metaAdId: string;
    metaAdName: string | null;
    metaAdSetId: string | null;
    metaAdSetName: string | null;
    metaCampaignId: string | null;
    metaCampaignName: string | null;
  }>;
}

interface SellerManagementStatsResponse {
  summary: Record<number, Record<string, number>>;
  totals: Record<string, number>;
  userMapping: Record<number, string>;
  topics: string[];
  topicStats: Record<string, Record<string, number>>;
}

export interface SellerDashboardLeadRow {
  id: number;
  clientId: number | null;
  clientName: string;
  clientPhone: string | null;
  clientEmail: string | null;
  clientRut: string | null;
  tags: string[];
  createdAt: string;
  assignedAt: string | null;
  closedAt: string | null;
  crmUpdatedAt: string | null;
  firstSellerMessageAt: string | null;
  avgResponseTimeSecs: number | null;
  lastMessageAt: string | null;
  sellerMessageCount: number;
  totalMessageCount: number;
  isUrgent: boolean;
  isOpen: boolean;
  firstContactReferenceAt: string | null;
  unansweredClientMessageCount: number;
}

export interface SellerDashboardSellerRow {
  id: number;
  name: string;
  urgentLeadCount: number;
  openLeadCount: number;
  unansweredLeadCount: number;
  avgCloseTimeDays: number;
  avgFirstMessageMins: number;
  avgResponseTimeMins: number;
  leadCount: number;
  leads: SellerDashboardLeadRow[];
}

export interface SellerDashboardResponse {
  sellers: SellerDashboardSellerRow[];
}

const leadsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getLeads: build.query<
      LeadsResponse,
      {
        businessUnitId?: number | null;
        paginationModel?: string;
        filterModel?: string;
        sortModel?: string;
        includeFullDetails?: boolean;
        searchTerm?: string;
      }
    >({
      query: (params) => ({
        url: '/leads',
        method: 'GET',
        params: {
          ...(params.businessUnitId ? { businessUnitId: params.businessUnitId } : {}),
          ...(params.paginationModel ? { paginationModel: params.paginationModel } : {}),
          ...(params.filterModel ? { filterModel: params.filterModel } : {}),
          ...(params.sortModel ? { sortModel: params.sortModel } : {}),
          ...(params.includeFullDetails ? { includeFullDetails: params.includeFullDetails } : {}),
          ...(params.searchTerm ? { searchTerm: params.searchTerm } : {}),
        },
      }),
      providesTags: (_result, _error, args) => [{ type: 'Leads', id: `BU-${args.businessUnitId || 'ALL'}` }],
      transformResponse: (response: LeadsResponse) => response,
    }),
    getLeadsKanban: build.query<
      KanbanLeadsResponse,
      {
        businessUnitId: number;
        startDate?: string;
        endDate?: string;
        pageSize?: number;
        statusPages?: Record<string, number>;
        filterModel?: string;
        searchTerm?: string;
      }
    >({
      query: (params) => ({
        url: '/leads/kanban',
        method: 'GET',
        params: {
          businessUnitId: params.businessUnitId,
          ...(params.startDate ? { startDate: params.startDate } : {}),
          ...(params.endDate ? { endDate: params.endDate } : {}),
          ...(params.pageSize ? { pageSize: params.pageSize } : {}),
          ...(params.statusPages ? { statusPages: JSON.stringify(params.statusPages) } : {}),
          ...(params.filterModel ? { filterModel: params.filterModel } : {}),
          ...(params.searchTerm ? { searchTerm: params.searchTerm } : {}),
        },
      }),
      providesTags: (_result, _error, args) => [{ type: 'Leads', id: `BU-${args.businessUnitId || 'ALL'}` }],
      transformResponse: (response: KanbanLeadsResponse) => response,
    }),
    getLeadsKanbanStatusCounts: build.query<
      KanbanStatusCountsResponse,
      {
        businessUnitId: number;
        startDate?: string;
        endDate?: string;
        filterModel?: string;
        searchTerm?: string;
      }
    >({
      query: (params) => ({
        url: '/leads/kanban/statusCounts',
        method: 'GET',
        params: {
          businessUnitId: params.businessUnitId,
          ...(params.startDate ? { startDate: params.startDate } : {}),
          ...(params.endDate ? { endDate: params.endDate } : {}),
          ...(params.filterModel ? { filterModel: params.filterModel } : {}),
          ...(params.searchTerm ? { searchTerm: params.searchTerm } : {}),
        },
      }),
      providesTags: (_result, _error, args) => [{ type: 'Leads', id: `BU-${args.businessUnitId || 'ALL'}` }],
      transformResponse: (response: KanbanStatusCountsResponse) => response,
    }),
    downloadLeads: build.mutation({
      query: (params: {
        businessUnitId?: number | null;
        paginationModel?: string;
        filterModel?: string;
        sortModel?: string;
        includeFullDetails?: boolean;
      }) => ({
        url: `/leads`,
        method: 'GET',
        params: {
          ...(params.businessUnitId ? { businessUnitId: params.businessUnitId } : {}),
          ...(params.paginationModel ? { paginationModel: params.paginationModel } : {}),
          ...(params.filterModel ? { filterModel: params.filterModel } : {}),
          ...(params.sortModel ? { sortModel: params.sortModel } : {}),
          ...(params.includeFullDetails ? { includeFullDetails: params.includeFullDetails } : {}),
        },
      }),
      transformResponse: (response: { rows: Lead[]; count: number }) => {
        return {
          leads: response.rows,
          total: response.count,
        };
      },
    }),
    getUniqueValuesOfLead: build.query({
      query: (params: {
        businessUnitId: number;
        startDate: string | null;
        endDate: string | null;
        attribute: string;
      }) => ({
        url: `/leads/uniqueValues`,
        method: 'GET',
        params: {
          ...(params.businessUnitId ? { businessUnitId: params.businessUnitId } : {}),
          ...(params.startDate ? { startDate: params.startDate } : {}),
          ...(params.endDate ? { endDate: params.endDate } : {}),
          ...(params.attribute ? { attribute: params.attribute } : {}),
        },
      }),
      transformResponse: (response: string[]) => response,
      // Filter-option lists (topics, sources, brands, campaigns) change rarely but are
      // backed by an expensive unscoped scan/unnest+DISTINCT on "Leads" (2-4s per call,
      // spills to disk on the read replica). The MasterFilter refetches them on every
      // mount; without caching this hammered the replica and stalled all reads. Keep the
      // cache warm for 10 min to collapse the request volume.
      keepUnusedDataFor: 600,
    }),
    getLeadMetaCampaignInfo: build.query<MetaCampaignInfo | null, { leadId: number }>({
      query: ({ leadId }) => ({
        url: `/leads/${leadId}/meta-campaign-info`,
        method: 'GET',
      }),
      transformResponse: (response: MetaCampaignInfo | null) => response,
    }),
    getCampaignStats: build.query<
      CampaignStatsResponse,
      {
        businessUnitId: number;
        startDate?: string | null;
        endDate?: string | null;
        campaignType?: 'whatsapp' | 'meta' | 'all';
        /** Restrict the Meta branch to campaigns belonging to these ad account external IDs (e.g. "act_123"). */
        metaAdAccountIds?: string[];
      }
    >({
      query: (params) => ({
        url: '/leads/campaignStats',
        method: 'GET',
        params: {
          businessUnitId: params.businessUnitId,
          ...(params.startDate ? { startDate: params.startDate } : {}),
          ...(params.endDate ? { endDate: params.endDate } : {}),
          ...(params.campaignType ? { campaignType: params.campaignType } : {}),
          ...(params.metaAdAccountIds && params.metaAdAccountIds.length > 0
            ? { metaAdAccountIds: params.metaAdAccountIds.join(',') }
            : {}),
        },
      }),
      transformResponse: (response: CampaignStatsResponse) => response,
    }),
    getCampaignSelector: build.query<
      CampaignSelectorResponse,
      {
        businessUnitId: number;
        startDate?: string | null;
        endDate?: string | null;
        campaignType?: 'whatsapp' | 'meta' | 'all';
      }
    >({
      query: (params) => ({
        url: '/leads/campaignSelector',
        method: 'GET',
        params: {
          businessUnitId: params.businessUnitId,
          ...(params.startDate ? { startDate: params.startDate } : {}),
          ...(params.endDate ? { endDate: params.endDate } : {}),
          ...(params.campaignType ? { campaignType: params.campaignType } : {}),
        },
      }),
      transformResponse: (response: CampaignSelectorResponse) => response,
    }),
    getSellerManagementStats: build.query<
      SellerManagementStatsResponse,
      {
        businessUnitId: number;
        startDate?: string;
        endDate?: string;
      }
    >({
      query: (params) => ({
        url: '/leads/seller-management-stats',
        method: 'GET',
        params: {
          businessUnitId: params.businessUnitId,
          ...(params.startDate ? { startDate: params.startDate } : {}),
          ...(params.endDate ? { endDate: params.endDate } : {}),
        },
      }),
      transformResponse: (response: SellerManagementStatsResponse) => response,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetLeadsQuery,
  useGetUniqueValuesOfLeadQuery,
  useDownloadLeadsMutation,
  useGetLeadsKanbanQuery,
  useGetLeadsKanbanStatusCountsQuery,
  useGetLeadMetaCampaignInfoQuery,
  useGetCampaignStatsQuery,
  useGetCampaignSelectorQuery,
  useGetSellerManagementStatsQuery,
} = leadsApi;
